/**
 * AdmissionPlanOfCare.jsx
 * Comprehensive hospice admission POC with automated interventions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  FileText, User, Pill, Stethoscope, Heart, Activity, Shield,
  AlertTriangle, CheckCircle, Clock, Plus, Trash2, ChevronRight,
  Brain, Users, Home, Loader2, Save, Download, RefreshCw,
  Clipboard, Calendar, Target, Zap, Bell, Settings, CalendarDays
} from 'lucide-react';
import POCTimelineView from './POCTimelineView';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Problem categories with icons
const PROBLEM_CATEGORIES = {
  pain: { label: 'Pain Management', icon: Activity, color: 'text-red-500' },
  respiratory: { label: 'Respiratory', icon: Heart, color: 'text-blue-500' },
  mobility: { label: 'Mobility/Fall Risk', icon: Users, color: 'text-orange-500' },
  skin: { label: 'Skin Integrity', icon: Shield, color: 'text-purple-500' },
  nutrition: { label: 'Nutrition/Hydration', icon: Pill, color: 'text-green-500' },
  elimination: { label: 'Bowel/Bladder', icon: Activity, color: 'text-yellow-500' },
  psychosocial: { label: 'Psychosocial', icon: Brain, color: 'text-pink-500' },
  spiritual: { label: 'Spiritual', icon: Heart, color: 'text-indigo-500' },
  safety: { label: 'Safety', icon: Home, color: 'text-gray-500' },
  end_of_life: { label: 'End of Life', icon: Heart, color: 'text-slate-500' }
};

// Discipline options
const DISCIPLINES = [
  { value: 'rn', label: 'RN (Registered Nurse)' },
  { value: 'lpn', label: 'LPN (Licensed Practical Nurse)' },
  { value: 'social_work', label: 'Social Worker' },
  { value: 'chaplain', label: 'Chaplain' },
  { value: 'aide', label: 'Hospice Aide' },
  { value: 'volunteer', label: 'Volunteer' },
  { value: 'physician', label: 'Physician' },
  { value: 'dietitian', label: 'Dietitian' },
  { value: 'physical_therapy', label: 'Physical Therapy' },
  { value: 'occupational_therapy', label: 'Occupational Therapy' },
  { value: 'speech_therapy', label: 'Speech Therapy' }
];

// Visit frequency options
const VISIT_FREQUENCIES = [
  '1x/week', '2x/week', '3x/week', 'Daily', 'Every 2 weeks', 'Monthly', 'PRN'
];

export default function AdmissionPlanOfCare({ patientId, referralId, onComplete, existingPocId }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(existingPocId ? 'timeline' : 'patient');
  const [problemTemplates, setProblemTemplates] = useState(null);
  const [dmeTemplates, setDmeTemplates] = useState(null);
  const [comfortKitTemplate, setComfortKitTemplate] = useState(null);
  const [createdPocId, setCreatedPocId] = useState(existingPocId || null);
  
  // POC State
  const [pocData, setPocData] = useState({
    patient_id: patientId || '',
    referral_id: referralId || '',
    admission_date: new Date().toISOString().split('T')[0],
    admission_type: 'new',
    level_of_care: 'routine',
    
    // Diagnosis
    primary_diagnosis: '',
    primary_icd10: '',
    secondary_diagnoses: [],
    
    // Prognosis
    prognosis_months: 6,
    prognosis_narrative: '',
    
    // Physicians
    attending_physician_name: '',
    attending_physician_npi: '',
    attending_physician_phone: '',
    hospice_physician_name: '',
    hospice_physician_npi: '',
    
    // Functional Status
    pps_score: 50,
    fast_stage: '',
    karnofsky_score: null,
    
    // Vital Signs
    vital_signs: {
      temperature: null,
      pulse: null,
      respirations: null,
      blood_pressure_systolic: null,
      blood_pressure_diastolic: null,
      oxygen_saturation: null,
      weight: null,
      pain_level: null
    },
    
    // Assessments
    fall_risk: {
      history_of_falls: false,
      falls_last_90_days: 0,
      gait_instability: false,
      cognitive_impairment: false,
      uses_assistive_device: false,
      device_type: '',
      medications_increase_risk: false,
      environmental_hazards: false,
      risk_score: 0,
      risk_level: 'low'
    },
    
    // Advance Directives
    advance_directives: {
      dnr: false,
      dnr_document_on_file: false,
      polst: false,
      polst_document_on_file: false,
      living_will: false,
      living_will_on_file: false,
      healthcare_proxy: false,
      proxy_name: '',
      proxy_phone: '',
      proxy_relationship: '',
      comfort_measures_only: true,
      no_hospitalization: false,
      no_911: false
    },
    
    // Problems (will auto-generate interventions)
    problems: [],
    
    // Medications
    medications: [],
    comfort_kit_ordered: true,
    
    // DME
    dme_orders: [],
    
    // Visit Schedule
    visit_schedule: [],
    
    // Additional
    allergies: [],
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    
    // Goals
    patient_goals: [],
    family_goals: [],
    
    // Education
    education_topics: []
  });
  
  // Generated interventions preview
  const [interventionsPreview, setInterventionsPreview] = useState([]);
  
  // Load templates
  useEffect(() => {
    loadTemplates();
  }, []);
  
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hospice_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [problemsRes, dmeRes, comfortKitRes] = await Promise.all([
        fetch(`${API_URL}/api/admission-poc/templates/problems`, { headers }),
        fetch(`${API_URL}/api/admission-poc/templates/dme`, { headers }),
        fetch(`${API_URL}/api/admission-poc/templates/comfort-kit`, { headers })
      ]);
      
      if (problemsRes.ok) setProblemTemplates(await problemsRes.json());
      if (dmeRes.ok) setDmeTemplates(await dmeRes.json());
      if (comfortKitRes.ok) setComfortKitTemplate(await comfortKitRes.json());
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Add problem and preview interventions
  const addProblem = (category) => {
    const template = problemTemplates?.templates?.[category];
    if (!template) return;
    
    const newProblem = {
      id: `problem_${Date.now()}`,
      category,
      description: template.description,
      onset_date: new Date().toISOString().split('T')[0],
      status: 'active',
      priority: 2,
      goals: template.goals || [],
      target_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    };
    
    setPocData(prev => ({
      ...prev,
      problems: [...prev.problems, newProblem]
    }));
    
    // Show intervention preview notification
    toast.success(`Problem added: ${template.description}`, {
      description: 'Interventions will be auto-generated when POC is saved'
    });
  };
  
  // Remove problem
  const removeProblem = (problemId) => {
    setPocData(prev => ({
      ...prev,
      problems: prev.problems.filter(p => p.id !== problemId)
    }));
  };
  
  // Add medication
  const addMedication = () => {
    const newMed = {
      id: `med_${Date.now()}`,
      name: '',
      dose: '',
      route: 'PO',
      frequency: '',
      indication: '',
      prescriber: '',
      start_date: new Date().toISOString().split('T')[0],
      is_comfort_kit: false,
      is_prn: false,
      prn_instructions: ''
    };
    
    setPocData(prev => ({
      ...prev,
      medications: [...prev.medications, newMed]
    }));
  };
  
  // Update medication
  const updateMedication = (index, field, value) => {
    setPocData(prev => {
      const meds = [...prev.medications];
      meds[index] = { ...meds[index], [field]: value };
      return { ...prev, medications: meds };
    });
  };
  
  // Remove medication
  const removeMedication = (index) => {
    setPocData(prev => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index)
    }));
  };
  
  // Add DME order
  const addDME = (item = null) => {
    const newDME = item || {
      id: `dme_${Date.now()}`,
      item: '',
      quantity: 1,
      justification: '',
      status: 'pending'
    };
    
    setPocData(prev => ({
      ...prev,
      dme_orders: [...prev.dme_orders, { ...newDME, id: `dme_${Date.now()}` }]
    }));
  };
  
  // Add visit schedule
  const addVisitSchedule = () => {
    const newSchedule = {
      discipline: 'rn',
      frequency: '2x/week',
      duration_minutes: 60,
      specific_days: [],
      start_date: pocData.admission_date
    };
    
    setPocData(prev => ({
      ...prev,
      visit_schedule: [...prev.visit_schedule, newSchedule]
    }));
  };
  
  // Calculate fall risk score
  const calculateFallRisk = () => {
    let score = 0;
    const fr = pocData.fall_risk;
    
    if (fr.history_of_falls) score += 2;
    if (fr.falls_last_90_days > 0) score += Math.min(fr.falls_last_90_days, 3);
    if (fr.gait_instability) score += 2;
    if (fr.cognitive_impairment) score += 2;
    if (fr.medications_increase_risk) score += 1;
    if (fr.environmental_hazards) score += 1;
    
    const risk_level = score >= 6 ? 'high' : score >= 3 ? 'moderate' : 'low';
    
    setPocData(prev => ({
      ...prev,
      fall_risk: { ...prev.fall_risk, risk_score: score, risk_level }
    }));
    
    // Auto-add mobility problem if high risk
    if (risk_level === 'high' && !pocData.problems.some(p => p.category === 'mobility')) {
      addProblem('mobility');
      toast.info('High fall risk detected - Mobility problem auto-added');
    }
  };
  
  // Save POC
  const savePOC = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('hospice_token');
      
      const response = await fetch(`${API_URL}/api/admission-poc/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pocData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create POC');
      }
      
      const result = await response.json();
      
      // Schedule IDG reviews every 2 weeks
      try {
        await fetch(`${API_URL}/api/admission-poc/${result.poc_id}/schedule-idg-reviews`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('IDG reviews scheduled every 14 days');
      } catch (idgError) {
        console.error('Failed to schedule IDG reviews:', idgError);
      }
      
      setCreatedPocId(result.poc_id);
      
      toast.success('Plan of Care Created!', {
        description: `${result.summary.interventions_generated} interventions auto-generated`
      });
      
      // Switch to timeline view after creation
      setActiveTab('timeline');
      
      if (onComplete) {
        onComplete(result);
      }
      
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save POC', { description: error.message });
    } finally {
      setSaving(false);
    }
  };
  
  // Update field helper
  const updateField = (path, value) => {
    setPocData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
        <span className="ml-2">Loading POC templates...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admission-poc">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <FileText className="h-6 w-6 text-teal-500" />
            Admission Plan of Care
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Complete hospice admission with automated intervention generation
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadTemplates}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reload Templates
          </Button>
          <Button 
            onClick={savePOC} 
            disabled={saving || !pocData.patient_id}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="save-poc-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Create POC & Generate Interventions
          </Button>
        </div>
      </div>

      {/* Auto-Generation Alert */}
      <Alert className="bg-teal-50 border-teal-200 dark:bg-teal-900/20 dark:border-teal-800">
        <Zap className="h-4 w-4 text-teal-600" />
        <AlertDescription className="text-teal-800 dark:text-teal-200">
          <strong>Automated Interventions:</strong> When you save this POC, the system will automatically 
          generate evidence-based interventions for each problem identified. Interventions include 
          discipline assignments, frequencies, documentation requirements, and expected outcomes.
        </AlertDescription>
      </Alert>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="patient" className="flex items-center gap-1">
            <User className="h-4 w-4" />
            Patient
          </TabsTrigger>
          <TabsTrigger value="clinical" className="flex items-center gap-1">
            <Stethoscope className="h-4 w-4" />
            Clinical
          </TabsTrigger>
          <TabsTrigger value="problems" className="flex items-center gap-1">
            <Target className="h-4 w-4" />
            Problems
            {pocData.problems.length > 0 && (
              <Badge variant="secondary" className="ml-1">{pocData.problems.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="medications" className="flex items-center gap-1">
            <Pill className="h-4 w-4" />
            Medications
          </TabsTrigger>
          <TabsTrigger value="dme" className="flex items-center gap-1">
            <Settings className="h-4 w-4" />
            DME
          </TabsTrigger>
          <TabsTrigger value="schedule" className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            Schedule
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-1" disabled={!createdPocId}>
            <CalendarDays className="h-4 w-4" />
            Timeline
            {createdPocId && <Badge variant="secondary" className="ml-1 text-xs">Live</Badge>}
          </TabsTrigger>
        </TabsList>

        {/* Patient Info Tab */}
        <TabsContent value="patient" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Admission Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Admission Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Admission Date</Label>
                    <Input 
                      type="date"
                      value={pocData.admission_date}
                      onChange={(e) => updateField('admission_date', e.target.value)}
                      data-testid="admission-date"
                    />
                  </div>
                  <div>
                    <Label>Admission Type</Label>
                    <Select 
                      value={pocData.admission_type}
                      onValueChange={(v) => updateField('admission_type', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New Admission</SelectItem>
                        <SelectItem value="transfer">Transfer</SelectItem>
                        <SelectItem value="readmission">Readmission</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label>Level of Care</Label>
                  <Select 
                    value={pocData.level_of_care}
                    onValueChange={(v) => updateField('level_of_care', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="routine">Routine Home Care (RHC)</SelectItem>
                      <SelectItem value="continuous">Continuous Home Care (CHC)</SelectItem>
                      <SelectItem value="inpatient">General Inpatient (GIP)</SelectItem>
                      <SelectItem value="respite">Inpatient Respite (IRC)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Diagnosis */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Primary Diagnosis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Terminal Diagnosis</Label>
                  <Input 
                    placeholder="e.g., Metastatic lung cancer"
                    value={pocData.primary_diagnosis}
                    onChange={(e) => updateField('primary_diagnosis', e.target.value)}
                    data-testid="primary-diagnosis"
                  />
                </div>
                <div>
                  <Label>ICD-10 Code</Label>
                  <Input 
                    placeholder="e.g., C34.90"
                    value={pocData.primary_icd10}
                    onChange={(e) => updateField('primary_icd10', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Prognosis (months)</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[pocData.prognosis_months]}
                      onValueChange={([v]) => updateField('prognosis_months', v)}
                      max={6}
                      min={1}
                      step={1}
                      className="flex-1"
                    />
                    <span className="font-semibold text-lg w-16">≤{pocData.prognosis_months} mo</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Physicians */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Attending Physician</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Physician Name</Label>
                  <Input 
                    placeholder="Dr. John Smith"
                    value={pocData.attending_physician_name}
                    onChange={(e) => updateField('attending_physician_name', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>NPI</Label>
                    <Input 
                      placeholder="1234567890"
                      value={pocData.attending_physician_npi}
                      onChange={(e) => updateField('attending_physician_npi', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input 
                      placeholder="(555) 123-4567"
                      value={pocData.attending_physician_phone}
                      onChange={(e) => updateField('attending_physician_phone', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Emergency Contact */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Emergency Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input 
                    placeholder="Jane Doe"
                    value={pocData.emergency_contact_name}
                    onChange={(e) => updateField('emergency_contact_name', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input 
                      placeholder="(555) 987-6543"
                      value={pocData.emergency_contact_phone}
                      onChange={(e) => updateField('emergency_contact_phone', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Relationship</Label>
                    <Input 
                      placeholder="Spouse"
                      value={pocData.emergency_contact_relationship}
                      onChange={(e) => updateField('emergency_contact_relationship', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Advance Directives */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-teal-500" />
                Advance Directives
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={pocData.advance_directives.dnr}
                      onCheckedChange={(v) => updateField('advance_directives.dnr', v)}
                    />
                    <Label>DNR on File</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={pocData.advance_directives.polst}
                      onCheckedChange={(v) => updateField('advance_directives.polst', v)}
                    />
                    <Label>POLST/MOLST</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={pocData.advance_directives.living_will}
                      onCheckedChange={(v) => updateField('advance_directives.living_will', v)}
                    />
                    <Label>Living Will</Label>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={pocData.advance_directives.healthcare_proxy}
                      onCheckedChange={(v) => updateField('advance_directives.healthcare_proxy', v)}
                    />
                    <Label>Healthcare Proxy</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={pocData.advance_directives.comfort_measures_only}
                      onCheckedChange={(v) => updateField('advance_directives.comfort_measures_only', v)}
                    />
                    <Label>Comfort Measures Only</Label>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={pocData.advance_directives.no_hospitalization}
                      onCheckedChange={(v) => updateField('advance_directives.no_hospitalization', v)}
                    />
                    <Label>No Hospitalization</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={pocData.advance_directives.no_911}
                      onCheckedChange={(v) => updateField('advance_directives.no_911', v)}
                    />
                    <Label>Do Not Call 911</Label>
                  </div>
                </div>
                {pocData.advance_directives.healthcare_proxy && (
                  <div className="space-y-2">
                    <Input 
                      placeholder="Proxy Name"
                      value={pocData.advance_directives.proxy_name}
                      onChange={(e) => updateField('advance_directives.proxy_name', e.target.value)}
                    />
                    <Input 
                      placeholder="Proxy Phone"
                      value={pocData.advance_directives.proxy_phone}
                      onChange={(e) => updateField('advance_directives.proxy_phone', e.target.value)}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clinical Assessment Tab */}
        <TabsContent value="clinical" className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Vital Signs */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Vital Signs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Temp (°F)</Label>
                    <Input 
                      type="number"
                      step="0.1"
                      placeholder="98.6"
                      value={pocData.vital_signs.temperature || ''}
                      onChange={(e) => updateField('vital_signs.temperature', parseFloat(e.target.value) || null)}
                    />
                  </div>
                  <div>
                    <Label>Pulse</Label>
                    <Input 
                      type="number"
                      placeholder="72"
                      value={pocData.vital_signs.pulse || ''}
                      onChange={(e) => updateField('vital_signs.pulse', parseInt(e.target.value) || null)}
                    />
                  </div>
                  <div>
                    <Label>Resp</Label>
                    <Input 
                      type="number"
                      placeholder="16"
                      value={pocData.vital_signs.respirations || ''}
                      onChange={(e) => updateField('vital_signs.respirations', parseInt(e.target.value) || null)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>BP Systolic</Label>
                    <Input 
                      type="number"
                      placeholder="120"
                      value={pocData.vital_signs.blood_pressure_systolic || ''}
                      onChange={(e) => updateField('vital_signs.blood_pressure_systolic', parseInt(e.target.value) || null)}
                    />
                  </div>
                  <div>
                    <Label>BP Diastolic</Label>
                    <Input 
                      type="number"
                      placeholder="80"
                      value={pocData.vital_signs.blood_pressure_diastolic || ''}
                      onChange={(e) => updateField('vital_signs.blood_pressure_diastolic', parseInt(e.target.value) || null)}
                    />
                  </div>
                  <div>
                    <Label>O2 Sat %</Label>
                    <Input 
                      type="number"
                      placeholder="96"
                      value={pocData.vital_signs.oxygen_saturation || ''}
                      onChange={(e) => updateField('vital_signs.oxygen_saturation', parseInt(e.target.value) || null)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Weight (lbs)</Label>
                    <Input 
                      type="number"
                      step="0.1"
                      placeholder="150"
                      value={pocData.vital_signs.weight || ''}
                      onChange={(e) => updateField('vital_signs.weight', parseFloat(e.target.value) || null)}
                    />
                  </div>
                  <div>
                    <Label>Pain Level (0-10)</Label>
                    <div className="flex items-center gap-2">
                      <Slider
                        value={[pocData.vital_signs.pain_level || 0]}
                        onValueChange={([v]) => updateField('vital_signs.pain_level', v)}
                        max={10}
                        min={0}
                        step={1}
                        className="flex-1"
                      />
                      <span className={`font-bold text-lg w-8 ${
                        (pocData.vital_signs.pain_level || 0) >= 7 ? 'text-red-500' :
                        (pocData.vital_signs.pain_level || 0) >= 4 ? 'text-orange-500' :
                        'text-green-500'
                      }`}>
                        {pocData.vital_signs.pain_level || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Functional Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Functional Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Palliative Performance Scale (PPS)</Label>
                  <div className="flex items-center gap-4">
                    <Slider
                      value={[pocData.pps_score]}
                      onValueChange={([v]) => updateField('pps_score', v)}
                      max={100}
                      min={0}
                      step={10}
                      className="flex-1"
                    />
                    <span className={`font-bold text-lg w-12 ${
                      pocData.pps_score <= 30 ? 'text-red-500' :
                      pocData.pps_score <= 50 ? 'text-orange-500' :
                      'text-green-500'
                    }`}>
                      {pocData.pps_score}%
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {pocData.pps_score <= 30 ? 'Totally bed bound, unable to do any activity' :
                     pocData.pps_score <= 50 ? 'Mainly sit/lie, considerable assistance needed' :
                     pocData.pps_score <= 70 ? 'Mainly sit/lie, unable to work, some assistance' :
                     'Ambulatory, normal to reduced activity'}
                  </p>
                </div>
                
                <div>
                  <Label>FAST Stage (Dementia)</Label>
                  <Select 
                    value={pocData.fast_stage || ''}
                    onValueChange={(v) => updateField('fast_stage', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select if applicable" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Not Applicable</SelectItem>
                      <SelectItem value="6a">6a - Difficulty dressing</SelectItem>
                      <SelectItem value="6b">6b - Difficulty bathing</SelectItem>
                      <SelectItem value="6c">6c - Difficulty toileting</SelectItem>
                      <SelectItem value="6d">6d - Urinary incontinence</SelectItem>
                      <SelectItem value="6e">6e - Fecal incontinence</SelectItem>
                      <SelectItem value="7a">7a - Speech limited to 6 words</SelectItem>
                      <SelectItem value="7b">7b - Speech limited to 1 word</SelectItem>
                      <SelectItem value="7c">7c - Ambulatory lost</SelectItem>
                      <SelectItem value="7d">7d - Cannot sit up</SelectItem>
                      <SelectItem value="7e">7e - Cannot smile</SelectItem>
                      <SelectItem value="7f">7f - Cannot hold head up</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Fall Risk Assessment */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Fall Risk Assessment</span>
                  <Badge className={
                    pocData.fall_risk.risk_level === 'high' ? 'bg-red-500' :
                    pocData.fall_risk.risk_level === 'moderate' ? 'bg-orange-500' :
                    'bg-green-500'
                  }>
                    {pocData.fall_risk.risk_level.toUpperCase()} RISK
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={pocData.fall_risk.history_of_falls}
                        onCheckedChange={(v) => {
                          updateField('fall_risk.history_of_falls', v);
                          setTimeout(calculateFallRisk, 100);
                        }}
                      />
                      <Label>History of Falls</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={pocData.fall_risk.gait_instability}
                        onCheckedChange={(v) => {
                          updateField('fall_risk.gait_instability', v);
                          setTimeout(calculateFallRisk, 100);
                        }}
                      />
                      <Label>Gait Instability</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={pocData.fall_risk.cognitive_impairment}
                        onCheckedChange={(v) => {
                          updateField('fall_risk.cognitive_impairment', v);
                          setTimeout(calculateFallRisk, 100);
                        }}
                      />
                      <Label>Cognitive Impairment</Label>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={pocData.fall_risk.uses_assistive_device}
                        onCheckedChange={(v) => updateField('fall_risk.uses_assistive_device', v)}
                      />
                      <Label>Uses Assistive Device</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={pocData.fall_risk.medications_increase_risk}
                        onCheckedChange={(v) => {
                          updateField('fall_risk.medications_increase_risk', v);
                          setTimeout(calculateFallRisk, 100);
                        }}
                      />
                      <Label>Meds Increase Risk</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        checked={pocData.fall_risk.environmental_hazards}
                        onCheckedChange={(v) => {
                          updateField('fall_risk.environmental_hazards', v);
                          setTimeout(calculateFallRisk, 100);
                        }}
                      />
                      <Label>Environmental Hazards</Label>
                    </div>
                  </div>
                </div>
                
                {pocData.fall_risk.history_of_falls && (
                  <div>
                    <Label>Falls in Last 90 Days</Label>
                    <Input 
                      type="number"
                      min="0"
                      value={pocData.fall_risk.falls_last_90_days}
                      onChange={(e) => {
                        updateField('fall_risk.falls_last_90_days', parseInt(e.target.value) || 0);
                        setTimeout(calculateFallRisk, 100);
                      }}
                    />
                  </div>
                )}
                
                <Button variant="outline" onClick={calculateFallRisk} className="w-full">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Calculate Risk Score
                </Button>
              </CardContent>
            </Card>

            {/* Allergies */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Allergies</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Textarea 
                    placeholder="Enter allergies (one per line)&#10;e.g., Penicillin - Rash&#10;Sulfa - Anaphylaxis&#10;Latex - Hives"
                    value={pocData.allergies.join('\n')}
                    onChange={(e) => updateField('allergies', e.target.value.split('\n').filter(a => a.trim()))}
                    rows={4}
                  />
                  {pocData.allergies.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {pocData.allergies.map((allergy, i) => (
                        <Badge key={i} variant="destructive">{allergy}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Problems Tab */}
        <TabsContent value="problems" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-teal-500" />
                Problem List
                <Badge className="ml-2">{pocData.problems.length} Problems</Badge>
              </CardTitle>
              <CardDescription>
                Select problems below. Interventions will be auto-generated for each problem when you save.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Problem Category Buttons */}
              <div className="grid grid-cols-5 gap-2 mb-6">
                {Object.entries(PROBLEM_CATEGORIES).map(([key, { label, icon: Icon, color }]) => (
                  <Button
                    key={key}
                    variant="outline"
                    className={`flex flex-col items-center p-3 h-auto ${
                      pocData.problems.some(p => p.category === key) ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : ''
                    }`}
                    onClick={() => addProblem(key)}
                    disabled={pocData.problems.some(p => p.category === key)}
                  >
                    <Icon className={`h-5 w-5 ${color}`} />
                    <span className="text-xs mt-1 text-center">{label}</span>
                    {pocData.problems.some(p => p.category === key) && (
                      <CheckCircle className="h-3 w-3 text-teal-500 mt-1" />
                    )}
                  </Button>
                ))}
              </div>

              {/* Current Problems */}
              <div className="space-y-3">
                {pocData.problems.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Target className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>No problems added yet</p>
                    <p className="text-sm">Click a category above to add a problem</p>
                  </div>
                ) : (
                  pocData.problems.map((problem, index) => {
                    const cat = PROBLEM_CATEGORIES[problem.category];
                    const Icon = cat?.icon || Target;
                    return (
                      <div 
                        key={problem.id}
                        className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-800"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`p-2 rounded-lg bg-white dark:bg-slate-700`}>
                              <Icon className={`h-5 w-5 ${cat?.color || 'text-slate-500'}`} />
                            </div>
                            <div>
                              <h4 className="font-semibold">{cat?.label || problem.category}</h4>
                              <p className="text-sm text-slate-600 dark:text-slate-300">
                                {problem.description}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                <span>Priority: {problem.priority === 1 ? 'High' : problem.priority === 2 ? 'Medium' : 'Low'}</span>
                                <span>Target: {problem.target_date}</span>
                              </div>
                              {problem.goals.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-xs font-medium text-slate-500">Goals:</p>
                                  <ul className="text-xs text-slate-600 dark:text-slate-400 list-disc list-inside">
                                    {problem.goals.slice(0, 2).map((goal, i) => (
                                      <li key={i}>{goal}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => removeProblem(problem.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        {/* Intervention Preview */}
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs font-medium text-teal-600 flex items-center gap-1">
                            <Zap className="h-3 w-3" />
                            Auto-generated interventions will include:
                          </p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {['RN Assessment', 'Aide Care', 'Education', 'Documentation'].map((tag, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Medications Tab */}
        <TabsContent value="medications" className="space-y-4 mt-4">
          <div className="grid grid-cols-3 gap-6">
            {/* Comfort Kit */}
            <Card className="col-span-1">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Pill className="h-5 w-5 text-teal-500" />
                  Comfort Kit
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-4">
                  <Checkbox 
                    checked={pocData.comfort_kit_ordered}
                    onCheckedChange={(v) => updateField('comfort_kit_ordered', v)}
                  />
                  <Label>Order Comfort Kit</Label>
                </div>
                
                {pocData.comfort_kit_ordered && comfortKitTemplate && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500">Standard comfort kit includes:</p>
                    {comfortKitTemplate.medications?.slice(0, 5).map((med, i) => (
                      <div key={i} className="text-xs p-2 bg-slate-50 dark:bg-slate-800 rounded">
                        <span className="font-medium">{med.name}</span>
                        <span className="text-slate-500 ml-1">{med.dose} {med.route}</span>
                      </div>
                    ))}
                    <p className="text-xs text-slate-400">+ 3 more medications</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Current Medications */}
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>Current Medications</span>
                  <Button size="sm" onClick={addMedication}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Medication
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {pocData.medications.map((med, index) => (
                      <div key={med.id} className="border rounded-lg p-3 bg-slate-50 dark:bg-slate-800">
                        <div className="grid grid-cols-4 gap-3">
                          <div className="col-span-2">
                            <Label className="text-xs">Medication Name</Label>
                            <Input 
                              placeholder="e.g., Morphine Sulfate"
                              value={med.name}
                              onChange={(e) => updateMedication(index, 'name', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Dose</Label>
                            <Input 
                              placeholder="e.g., 15mg"
                              value={med.dose}
                              onChange={(e) => updateMedication(index, 'dose', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Route</Label>
                            <Select 
                              value={med.route}
                              onValueChange={(v) => updateMedication(index, 'route', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="PO">PO (Oral)</SelectItem>
                                <SelectItem value="SL">SL (Sublingual)</SelectItem>
                                <SelectItem value="PR">PR (Rectal)</SelectItem>
                                <SelectItem value="SQ">SQ (Subcutaneous)</SelectItem>
                                <SelectItem value="IV">IV</SelectItem>
                                <SelectItem value="IM">IM</SelectItem>
                                <SelectItem value="Topical">Topical</SelectItem>
                                <SelectItem value="Patch">Patch</SelectItem>
                                <SelectItem value="Inhaled">Inhaled</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mt-2">
                          <div>
                            <Label className="text-xs">Frequency</Label>
                            <Input 
                              placeholder="e.g., Q4H PRN"
                              value={med.frequency}
                              onChange={(e) => updateMedication(index, 'frequency', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Indication</Label>
                            <Input 
                              placeholder="e.g., Pain"
                              value={med.indication}
                              onChange={(e) => updateMedication(index, 'indication', e.target.value)}
                            />
                          </div>
                          <div className="flex items-end gap-2">
                            <div className="flex items-center gap-2">
                              <Checkbox 
                                checked={med.is_prn}
                                onCheckedChange={(v) => updateMedication(index, 'is_prn', v)}
                              />
                              <Label className="text-xs">PRN</Label>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 ml-auto"
                              onClick={() => removeMedication(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {pocData.medications.length === 0 && (
                      <div className="text-center py-8 text-slate-500">
                        <Pill className="h-12 w-12 mx-auto mb-2 opacity-30" />
                        <p>No medications added yet</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* DME Tab */}
        <TabsContent value="dme" className="space-y-4 mt-4">
          <div className="grid grid-cols-3 gap-6">
            {/* Quick Add DME */}
            <Card className="col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Quick Add DME</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dmeTemplates?.standard_items?.map((item, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      className="w-full justify-start text-sm"
                      onClick={() => addDME(item)}
                    >
                      <Plus className="h-3 w-3 mr-2" />
                      {item.item}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* DME Orders */}
            <Card className="col-span-2">
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  <span>DME Orders</span>
                  <Button size="sm" onClick={() => addDME()}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Custom DME
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pocData.dme_orders.map((dme, index) => (
                    <div key={dme.id} className="flex items-center gap-3 border rounded-lg p-3">
                      <div className="flex-1">
                        <Input 
                          placeholder="DME Item"
                          value={dme.item}
                          onChange={(e) => {
                            const orders = [...pocData.dme_orders];
                            orders[index] = { ...orders[index], item: e.target.value };
                            updateField('dme_orders', orders);
                          }}
                        />
                      </div>
                      <div className="w-20">
                        <Input 
                          type="number"
                          min="1"
                          value={dme.quantity}
                          onChange={(e) => {
                            const orders = [...pocData.dme_orders];
                            orders[index] = { ...orders[index], quantity: parseInt(e.target.value) || 1 };
                            updateField('dme_orders', orders);
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <Input 
                          placeholder="Medical Justification"
                          value={dme.justification}
                          onChange={(e) => {
                            const orders = [...pocData.dme_orders];
                            orders[index] = { ...orders[index], justification: e.target.value };
                            updateField('dme_orders', orders);
                          }}
                        />
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-500"
                        onClick={() => {
                          updateField('dme_orders', pocData.dme_orders.filter((_, i) => i !== index));
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  
                  {pocData.dme_orders.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <Settings className="h-12 w-12 mx-auto mb-2 opacity-30" />
                      <p>No DME orders added yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Schedule Tab */}
        <TabsContent value="schedule" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-teal-500" />
                  Visit Schedule
                </span>
                <Button size="sm" onClick={addVisitSchedule}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Discipline
                </Button>
              </CardTitle>
              <CardDescription>
                Configure discipline visits. CMS requires RN visit every 14 days minimum.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pocData.visit_schedule.map((schedule, index) => (
                  <div key={index} className="grid grid-cols-5 gap-4 items-end border rounded-lg p-4">
                    <div>
                      <Label>Discipline</Label>
                      <Select 
                        value={schedule.discipline}
                        onValueChange={(v) => {
                          const schedules = [...pocData.visit_schedule];
                          schedules[index] = { ...schedules[index], discipline: v };
                          updateField('visit_schedule', schedules);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DISCIPLINES.map(d => (
                            <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Frequency</Label>
                      <Select 
                        value={schedule.frequency}
                        onValueChange={(v) => {
                          const schedules = [...pocData.visit_schedule];
                          schedules[index] = { ...schedules[index], frequency: v };
                          updateField('visit_schedule', schedules);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {VISIT_FREQUENCIES.map(f => (
                            <SelectItem key={f} value={f}>{f}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Duration (min)</Label>
                      <Input 
                        type="number"
                        value={schedule.duration_minutes}
                        onChange={(e) => {
                          const schedules = [...pocData.visit_schedule];
                          schedules[index] = { ...schedules[index], duration_minutes: parseInt(e.target.value) || 60 };
                          updateField('visit_schedule', schedules);
                        }}
                      />
                    </div>
                    <div>
                      <Label>Start Date</Label>
                      <Input 
                        type="date"
                        value={schedule.start_date}
                        onChange={(e) => {
                          const schedules = [...pocData.visit_schedule];
                          schedules[index] = { ...schedules[index], start_date: e.target.value };
                          updateField('visit_schedule', schedules);
                        }}
                      />
                    </div>
                    <Button 
                      variant="ghost" 
                      className="text-red-500"
                      onClick={() => {
                        updateField('visit_schedule', pocData.visit_schedule.filter((_, i) => i !== index));
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                
                {pocData.visit_schedule.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    <Calendar className="h-12 w-12 mx-auto mb-2 opacity-30" />
                    <p>No discipline visits scheduled yet</p>
                    <p className="text-sm">Click "Add Discipline" to configure visit schedule</p>
                  </div>
                )}
                
                {/* CMS Requirement Reminder */}
                {pocData.visit_schedule.length > 0 && !pocData.visit_schedule.some(s => s.discipline === 'rn') && (
                  <Alert className="bg-yellow-50 border-yellow-200">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-800">
                      <strong>CMS Requirement:</strong> RN visit required at least every 14 days per §418.76
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Goals of Care */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Goals of Care</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label>Patient Goals</Label>
                  <Textarea 
                    placeholder="Enter patient's goals (one per line)&#10;e.g., Remain at home&#10;Be pain-free&#10;Spend time with family"
                    value={pocData.patient_goals.join('\n')}
                    onChange={(e) => updateField('patient_goals', e.target.value.split('\n').filter(g => g.trim()))}
                    rows={4}
                  />
                </div>
                <div>
                  <Label>Family Goals</Label>
                  <Textarea 
                    placeholder="Enter family's goals (one per line)&#10;e.g., Keep loved one comfortable&#10;Understand what to expect&#10;Know how to manage symptoms"
                    value={pocData.family_goals.join('\n')}
                    onChange={(e) => updateField('family_goals', e.target.value.split('\n').filter(g => g.trim()))}
                    rows={4}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="space-y-4 mt-4">
          {createdPocId ? (
            <POCTimelineView 
              pocId={createdPocId}
              patientName={pocData.patient_id}
            />
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <CalendarDays className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-semibold text-slate-600">Timeline Not Available</h3>
                <p className="text-slate-500 mt-2">
                  Create the Plan of Care first to view the intervention timeline.
                </p>
                <Button 
                  className="mt-4"
                  onClick={() => setActiveTab('patient')}
                >
                  Start POC Creation
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Summary Footer */}
      <Card className="bg-slate-50 dark:bg-slate-800">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm">
              <div>
                <span className="text-slate-500">Problems:</span>
                <span className="font-semibold ml-1">{pocData.problems.length}</span>
              </div>
              <div>
                <span className="text-slate-500">Medications:</span>
                <span className="font-semibold ml-1">
                  {pocData.medications.length + (pocData.comfort_kit_ordered ? 8 : 0)}
                </span>
              </div>
              <div>
                <span className="text-slate-500">DME Orders:</span>
                <span className="font-semibold ml-1">{pocData.dme_orders.length}</span>
              </div>
              <div>
                <span className="text-slate-500">Disciplines:</span>
                <span className="font-semibold ml-1">{pocData.visit_schedule.length}</span>
              </div>
            </div>
            
            <Button 
              onClick={savePOC} 
              disabled={saving || !pocData.patient_id || pocData.problems.length === 0}
              className="bg-teal-600 hover:bg-teal-700"
              size="lg"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Zap className="h-4 w-4 mr-2" />
              )}
              Create POC & Auto-Generate Interventions
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
